import Dialog from '@mui/material/Dialog'
import DialogTitle from '@mui/material/DialogTitle'
import DialogContent from '@mui/material/DialogContent'
import DialogActions from '@mui/material/DialogActions'
import TextField from '@mui/material/TextField'
import Switch from '@mui/material/Switch'
import FormControlLabel from '@mui/material/FormControlLabel'
import Button from '@mui/material/Button'
import Box from '@mui/material/Box'
import FormLabel from '@mui/material/FormLabel'
import Typography from '@mui/material/Typography'
import { isJSON } from '../../utils'
import KeyValueFieldsEditor from '../common/KeyValueFieldsEditor'

/**
 * EditConfigDialog component renders a dialog for editing configuration settings.
 *
 * @param {Object} props - The properties passed to the component.
 * @param {boolean} props.open - Determines whether the dialog is open or closed.
 * @param {Object} props.config - The configuration object to be edited.
 * @param {string} props.config.key - The key of the configuration.
 * @param {string} [props.config.value] - The value of the configuration.
 * @param {string} [props.config.comment] - The comment associated with the configuration.
 * @param {boolean} [props.config.encrypted] - Indicates if the value is encrypted.
 * @param {string} props.valueMode - The mode for editing the value (e.g., 'text' or 'json').
 * @param {Array} props.keyValueFields - The key-value fields for JSON mode editing.
 * @param {boolean} props.isLoading - Indicates if the save operation is in progress.
 * @param {Function} props.onClose - Callback function to handle dialog close.
 * @param {Function} props.onChange - Callback function to handle changes in input fields.
 * @param {Function} props.onValueModeSwitch - Callback function to switch between value modes.
 * @param {Function} props.onKeyValueFieldChange - Callback function to handle changes in key-value fields.
 * @param {Function} props.onRemoveKeyValueField - Callback function to remove a key-value field.
 * @param {Function} props.onAddKeyValueField - Callback function to add a new key-value field.
 * @param {Function} props.onSave - Callback function to handle saving the configuration.
 *
 * @returns {JSX.Element} The rendered EditConfigDialog component.
 */

export default function EditConfigDialog(props) {
  const {
    open,
    config,
    valueMode,
    keyValueFields,
    isLoading,
    onClose,
    onChange,
    onValueModeSwitch,
    onKeyValueFieldChange,
    onRemoveKeyValueField,
    onAddKeyValueField,
    onSave,
  } = props

  return (
    <Dialog open={open} onClose={onClose} maxWidth="md" fullWidth>
      <DialogTitle>
        {config?.key?.toLowerCase().startsWith('tcp-')
          ? 'Edit TCP Config'
          : 'Edit Monitor Config'}
      </DialogTitle>
      <DialogContent>
        <TextField
          margin="dense"
          label="Key"
          fullWidth
          value={config?.key || ''}
          onChange={onChange('key')}
          disabled
        />
        {/* Value field with JSON mode switch inline */}
        {isJSON(config?.value) ? (
          <Box
            sx={{
              mb: 2,
              border: '1px solid',
              borderColor: 'divider',
              borderRadius: 1,
              p: 2,
              mt: 2,
            }}
          >
            <Box
              sx={{
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                mb: 1,
              }}
            >
              <FormLabel variant="body2" sx={{ fontWeight: 500, mr: 1 }}>
                Value
              </FormLabel>
              <FormControlLabel
                control={
                  <Switch
                    checked={valueMode === 'text'}
                    onChange={onValueModeSwitch}
                    color="primary"
                    size="small"
                  />
                }
                label={
                  <Typography variant="caption">Edit in JSON Mode</Typography>
                }
                sx={{ ml: 0 }}
              />
            </Box>
            {valueMode === 'text' ? (
              <TextField
                margin="dense"
                label="Value (JSON or text)"
                fullWidth
                multiline
                minRows={3}
                value={config?.value || ''}
                onChange={onChange('value')}
              />
            ) : (
              <KeyValueFieldsEditor
                fields={keyValueFields}
                onFieldChange={onKeyValueFieldChange}
                onRemoveField={onRemoveKeyValueField}
                onAddField={onAddKeyValueField}
              />
            )}
          </Box>
        ) : (
          <TextField
            margin="dense"
            label="Value"
            fullWidth
            multiline
            minRows={3}
            value={config?.value || ''}
            onChange={onChange('value')}
          />
        )}
        <TextField
          margin="dense"
          label="Comment"
          fullWidth
          value={config?.comment || ''}
          onChange={onChange('comment')}
        />
        <FormControlLabel
          control={
            <Switch
              checked={!!config?.encrypted}
              onChange={onChange('encrypted')}
              color="primary"
            />
          }
          label="Encrypt Value"
        />
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Cancel</Button>
        <Button
          onClick={onSave}
          variant="contained"
          color="primary"
          disabled={isLoading}
        >
          Save
        </Button>
      </DialogActions>
    </Dialog>
  )
}
